/**
 * Listing JSON-LD — BreadcrumbList + Product/Car with its Offer.
 *
 * ONE builder for both writers, like blogJsonLd.js:
 *   - pages/buyer/VehicleDetail.js hands it to useSEO at runtime, and
 *   - scripts/inject-seo-heads.js writes it into the static head at build time,
 *     which is the only copy a crawler that never runs JS sees.
 *
 * These two were maintained as parallel copies with a comment asking future
 * edits to keep them in step. That is exactly the arrangement that produced two
 * contradictory article nodes on all 133 blog posts before blogJsonLd.js, so it
 * is collapsed here before it drifts rather than after.
 *
 * Keep the file import-free and every declaration a top-level export — the
 * injector loads it by stripping the `export` keywords. Anything needing an
 * import (the canonical URL, which comes from vehiclePath) is passed in.
 */
export const ORGANIZATION_ID = 'https://www.buyselltaxi.com/#organization';

/**
 * @param {object} v        a listing row from /api/buyer/vehicles
 * @param {object} opts
 * @param {string} opts.url         absolute canonical URL for the listing
 * @param {string} opts.description meta description, used when the row has none
 * @param {string} [opts.image]     unused — kept so callers need not change;
 *                                  see the note on `image` below
 * @param {string} opts.baseUrl     site origin
 * @param {object} [opts.cityPage]  from utils/cityPage.js; when present the
 *                                  breadcrumb runs through the city page
 */
export function vehicleJsonLd(v, { url, description, image, baseUrl, cityPage }) {
  const images = Array.isArray(v.images) ? v.images.filter(Boolean) : [];
  const loc = [v.city, v.state].filter(Boolean).join(', ');
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: baseUrl },
        cityPage
          ? { '@type': 'ListItem', position: 2, name: `Used Taxis in ${cityPage.name}`, item: `${baseUrl}${cityPage.path}` }
          : { '@type': 'ListItem', position: 2, name: 'Browse Vehicles', item: `${baseUrl}/vehicles` },
        { '@type': 'ListItem', position: 3, name: v.title, item: url },
      ],
    },
    {
      '@context': 'https://schema.org',
      // Google ignores a bare Car for product rich results — the dual type is
      // what made these count as valid items at all.
      '@type': ['Product', 'Car'],
      name: v.title,
      description: v.description || description,
      // Only real photos. Five listings have none, and they used to carry the
      // site's og-image card as the product image — a logo standing in for the
      // car, which Google's product image guidelines treat as misrepresentation.
      // No image is better than the wrong one; og:image keeps the card for
      // social previews.
      image: images.length ? images : undefined,
      url,
      brand: { '@type': 'Brand', name: v.brand },
      model: v.model,
      vehicleModelDate: v.year ? String(v.year) : undefined,
      itemCondition: 'https://schema.org/UsedCondition',
      color: v.color || undefined,
      fuelType: v.fuel_type || undefined,
      vehicleTransmission: v.transmission || undefined,
      mileageFromOdometer: v.km_run
        ? { '@type': 'QuantitativeValue', value: v.km_run, unitCode: 'KMT' }
        : undefined,
      offers: {
        '@type': 'Offer',
        priceCurrency: 'INR',
        price: v.price_final,
        availability:
          v.status === 'sold' ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/UsedCondition',
        url,
        // Reference the sitewide entity rather than restating it: index.html
        // already ships Organization + AutoDealer under this @id.
        seller: { '@id': ORGANIZATION_ID },
        // Where the car actually is. A used vehicle is collected in person, so
        // this is the one fact that makes a listing answerable for "near me"
        // and for a buyer deciding whether the drive is worth it. City and
        // state are the only location the listings table holds — no street
        // address is invented here.
        ...(v.city
          ? {
              availableAtOrFrom: {
                '@type': 'Place',
                address: {
                  '@type': 'PostalAddress',
                  addressLocality: v.city,
                  ...(v.state ? { addressRegion: v.state } : {}),
                  addressCountry: 'IN',
                },
              },
            }
          : {}),
      },
      additionalProperty: [
        { '@type': 'PropertyValue', name: 'Category', value: v.category },
        { '@type': 'PropertyValue', name: 'Location', value: loc },
      ].filter((p) => p.value),
    },
  ];
}
